<script>
import { ref, onMounted, computed } from 'vue';
import StampCardCalendar from './StampCardCalendar.vue';

/**
 * スタンプカード メイン (専用ページ + モーダル両対応)。
 *
 * 仕様: _docs/event/130-spec.md §4, §8
 *
 * - onMounted: claim API 叩いて当該帯のスタンプを取得
 * - 取得結果に応じて新規セルに落下アニメ発火
 * - status API でカレンダー全体の状態を取得・表示
 *
 * Props:
 *   - mode: 'page' | 'modal' (デフォルト 'page')
 *
 * Events:
 *   - close: モーダル閉じる
 */
export default {
    name: 'StampCard',
    components: { StampCardCalendar },
    props: {
        mode: { type: String, default: 'page' },
        headerImageUrl: { type: String, default: '' },
        ajisaiImageUrl: { type: String, default: '' },
    },
    emits: ['close'],
    setup(props, { emit }) {
        const loading = ref(true);
        const error = ref('');
        const balance = ref(0);
        const startDate = ref('');
        const endDate = ref('');
        const history = ref({});
        const milestones = ref([]);
        const newCell = ref(null);     // {date, slot} 新規取得セル
        const claimResult = ref(null);  // claim() 結果

        async function loadStatus() {
            try {
                const res = await window.axios.post('/api/v1/event/stamp/status', {});
                if (res.data.header.status === 'success') {
                    const r = res.data.result;
                    balance.value = r.balance;
                    startDate.value = r.start_date;
                    endDate.value = r.end_date;
                    history.value = r.history || {};
                    milestones.value = r.milestones || [];
                } else {
                    error.value = res.data.result?.message || '取得失敗';
                }
            } catch (e) {
                error.value = e.message || '通信エラー';
            }
        }

        async function claimNow() {
            try {
                const res = await window.axios.post('/api/v1/event/stamp/claim', {});
                if (res.data.header.status === 'success') {
                    claimResult.value = res.data.result;
                    // 新規取得セルを記録 (アニメ用)
                    if (claimResult.value.granted && claimResult.value.slot) {
                        const today = new Date();
                        const yyyy = today.getFullYear();
                        const mm = String(today.getMonth() + 1).padStart(2, '0');
                        const dd = String(today.getDate()).padStart(2, '0');
                        newCell.value = {
                            date: `${yyyy}-${mm}-${dd}`,
                            slot: claimResult.value.slot,
                        };
                    }
                }
            } catch (e) {
                // claim 失敗時はサイレント (status はそのまま表示)
                if (window.console) console.warn('[stamp] claim failed', e);
            }
        }

        onMounted(async () => {
            await claimNow();
            await loadStatus();
            loading.value = false;
        });

        const milestonesProgress = computed(() => {
            return milestones.value.map((m) => ({
                ...m,
                progress: Math.min(balance.value / m.threshold, 1),
                remaining: Math.max(m.threshold - balance.value, 0),
            }));
        });

        return {
            loading, error, balance, startDate, endDate, history, milestones, milestonesProgress, newCell, claimResult,
            onClose: () => emit('close'),
        };
    },
};
</script>

<template>
    <div class="stamp-card-container" :class="mode">
        <div v-if="mode === 'modal'" class="stamp-card-modal-header">
            <h3>あじさいデート2026 スタンプカード</h3>
            <button type="button" class="close-btn" @click="onClose">×</button>
        </div>
        <div v-else class="stamp-card-page-header">
            <img v-if="headerImageUrl" :src="headerImageUrl" alt="あじさいデート2026 スタンプカード" class="stamp-header-image">
            <h2 v-else>あじさいデート2026 スタンプカード</h2>
        </div>

        <div v-if="loading" class="loading">読み込み中...</div>
        <div v-else-if="error" class="error">{{ error }}</div>
        <div v-else>
            <div class="stamp-summary">
                <span class="balance">スタンプ {{ balance }} 個</span>
                <span v-if="claimResult && claimResult.granted" class="newly-acquired">
                    新しいスタンプを獲得しました！
                </span>
            </div>

            <StampCardCalendar
                :start-date="startDate"
                :end-date="endDate"
                :history="history"
                :new-cell="newCell"
                :ajisai-image-url="ajisaiImageUrl"
            />

            <div class="milestones">
                <h4>達成報酬</h4>
                <ul>
                    <li v-for="m in milestonesProgress" :key="m.threshold" :class="{ reached: m.reached }">
                        <img v-if="m.reward_image_url" :src="m.reward_image_url" :alt="m.reward_name" class="milestone-img">
                        <span class="milestone-label">{{ m.threshold }}個: {{ m.reward_name }}</span>
                        <span v-if="m.reached" class="milestone-status">✓ 獲得済み</span>
                        <span v-else class="milestone-status">あと {{ m.remaining }} 個</span>
                    </li>
                </ul>
            </div>

            <div v-if="mode === 'page'" class="actions">
                <a href="/" class="btn-home">ホームへ戻る</a>
            </div>
        </div>
    </div>
</template>

<style scoped>
.stamp-card-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 16px;
    background: linear-gradient(135deg, #f3f9fc 0%, #ffffff 45%, #fdf3f5 100%);
    border-radius: 8px;
}
.stamp-card-container.modal {
    border: 1px solid #b8a9d9;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.stamp-card-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}
.stamp-card-page-header {
    text-align: center;
    margin-bottom: 12px;
}
.stamp-header-image {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 0 auto;
}
.close-btn {
    background: transparent;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #666;
}
.stamp-summary {
    text-align: center;
    margin: 12px 0;
    font-weight: bold;
}
.balance {
    font-size: 18px;
    color: #4a8fa8;
    margin-right: 12px;
}
.newly-acquired {
    color: #f00;
    animation: pulse 1.5s ease-in-out 2;
}
@keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
}
.milestones {
    margin-top: 20px;
    padding: 12px;
    background: #fff;
    border-radius: 6px;
}
.milestones h4 {
    margin: 0 0 8px 0;
    font-size: 14px;
    color: #555;
}
.milestones ul {
    list-style: none;
    padding: 0;
    margin: 0;
}
.milestones li {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border-bottom: 1px solid #f0f0f0;
    font-size: 13px;
}
.milestones .milestone-img {
    width: 32px;
    height: 32px;
    object-fit: contain;
    flex-shrink: 0;
}
.milestones .milestone-label {
    flex: 1;
}
.milestones li.reached {
    background: #f0fff4;
    color: #2c7a3f;
}
.milestones li.reached .milestone-status {
    color: #2c7a3f;
    font-weight: bold;
}
.actions {
    text-align: center;
    margin-top: 20px;
}
.btn-home {
    display: inline-block;
    padding: 8px 20px;
    background: #fff;
    border: 1px solid #b8a9d9;
    border-radius: 999px;
    color: #4a8fa8;
    text-decoration: none;
    font-size: 13px;
    font-weight: bold;
}
.loading, .error {
    text-align: center;
    padding: 20px;
    color: #666;
}
.error {
    color: #c00;
}
</style>
